import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useTelegram } from './hooks/useTelegram';
import { useAppContext } from './hooks/useAppContext';
import { usePWA } from './hooks/usePWA';
import { getEnvironmentConfig } from './utils/environment';
import { MainLayout } from './layouts/MainLayout';
import { DealsScreen } from './screens/Deals';
import { DatabaseScreen } from './screens/Database';
import { ProjectDetailsScreen } from './screens/Database/ProjectDetailsScreen';
import PortfolioScreen, { CreatePortfolioAssetScreen, EditPortfolioAssetScreen } from './screens/Portfolio';
import PortfolioDetailScreen from './screens/Portfolio/PortfolioDetailScreen';
import AssetDetailScreen from './screens/AssetDetailScreen/AssetDetailScreen';
import { PlaceInquiryScreen } from './screens/PlaceInquiry/PlaceInquiryScreen';
import { PlaceRFQScreen } from './screens/PlaceRFQ/PlaceRFQScreen';
import { AttestationScreen } from './screens/Attestation/AttestationScreen';
import { WelcomeScreen } from './screens/Welcome';
import { NotificationsScreen, NotificationSettingsScreen } from './screens/Notifications';
import { Loader } from './components/Loader';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { AppContextProvider } from './components/AppContextProvider';
import { ToastProvider } from './contexts/ToastContext';
import { apiService } from './utils/api';
import { SignupScreen, LoginScreen } from './screens/Auth';
import { MobilePWALandingScreen, PWALandingScreenDesktop } from './screens/PWALanding';
import { MobilePWALandingScreen as MobilePWALandingScreenMobile } from './screens/PWALanding/Mobile';
import ForgotPasswordScreen from './screens/Auth/ForgotPasswordScreen';

// Global flag for App.tsx
let isAppInitialized = false;

function App() {
  const { webApp, isReady, isTelegram } = useTelegram();
  const { appContext, isMobileBrowser, isDesktopBrowser } = useAppContext();
  const { isInstalled: isPWAInstalled } = usePWA();
  const [isAttested, setIsAttested] = useState<boolean | null>(null);
  const [isCheckingAttestation, setIsCheckingAttestation] = useState(false); // Start with false
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const { enableTelegramFeatures, isPWA, isBrowser } = getEnvironmentConfig();

  // Domain detection
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isPWADomain = hostname === 'pwa.secondlane.io' || hostname === 'dev-pwa.secondlane.io' || hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '192.168.1.4';
  const isMobileDomain = hostname === 'm.secondlane.io' || hostname === 'dev-m.secondlane.io' || hostname === '192.168.1.4' || hostname === '192.168.0.106';

  // Log app context for debugging
  useEffect(() => {
    // console.log('App context:', appContext);
    // console.log('PWA Installed:', isPWAInstalled);
  }, [appContext, isPWAInstalled]);

  // Check if user is authenticated (for PWA/browser)
  useEffect(() => {
    const checkAuth = () => {
      if (isPWA || isBrowser) {
        const token = localStorage.getItem('authToken');
        setIsAuthenticated(!!token);
      } else if (isTelegram) {
        // In Telegram, we don't use JWT authentication
        setIsAuthenticated(true);
      }
    };

    // Initial check
    checkAuth();

    // Set up storage event listener for PWA/browser
    if (isPWA || isBrowser) {
      const handleStorageChange = (e: StorageEvent) => {
        if (e.key === 'authToken') {
          checkAuth();
        }
      };

      window.addEventListener('storage', handleStorageChange);
      return () => window.removeEventListener('storage', handleStorageChange);
    }
  }, [isPWA, isBrowser, isTelegram]);

  // Check attestation status only if authenticated
  useEffect(() => {
    if (isAuthenticated === false) {
      setIsCheckingAttestation(false);
      return;
    }

    const checkAttestationStatus = async () => {
      try {
        setIsCheckingAttestation(true);
        const response = await apiService.getAttestationStatus();
        setIsAttested(response.is_attested);
      } catch (error: unknown) {
        console.error('Error checking attestation status:', error);
        
        // Check if this is an authentication error (401/403)
        const isAuthError = error && 
          typeof error === 'object' && 
          ('status' in error || 'response' in error) &&
          ((error as { status?: number }).status === 401 || 
           (error as { status?: number }).status === 403 ||
           (error as { response?: { status?: number } }).response?.status === 401 || 
           (error as { response?: { status?: number } }).response?.status === 403);
        
        if (isAuthError) {
          // console.log('Authentication error detected, clearing auth state');
          // Token is invalid/expired, clear authentication state
          localStorage.removeItem('authToken');
          setIsAuthenticated(false);
          setIsAttested(null);
        } else {
          // For other errors (like network/CORS), we can't determine attestation status
          // Don't change the current attestation status to avoid unnecessary redirects
          // console.log('Non-authentication error (likely network/CORS), keeping current attestation status');
          // Don't set setIsAttested(false) here to avoid redirecting to welcome screen
          // Also don't set it to null or any other value - just leave it as is
        }
      } finally {
        setIsCheckingAttestation(false);
      }
    };

    checkAttestationStatus();
  }, [isAuthenticated]);

  // Add a function to refresh attestation status
  const refreshAttestationStatus = async () => {
    if (isAuthenticated === false) return;
    
    try {
      setIsCheckingAttestation(true);
      const response = await apiService.getAttestationStatus();
      setIsAttested(response.is_attested);
    } catch (error: unknown) {
      console.error('Error refreshing attestation status:', error);
      
      // Check if this is an authentication error (401/403)
      const isAuthError = error && 
        typeof error === 'object' && 
        ('status' in error || 'response' in error) &&
        ((error as { status?: number }).status === 401 || 
         (error as { status?: number }).status === 403 ||
         (error as { response?: { status?: number } }).response?.status === 401 || 
         (error as { response?: { status?: number } }).response?.status === 403);
      
      if (isAuthError) {
        // console.log('Authentication error detected in refresh, clearing auth state');
        // Token is invalid/expired, clear authentication state
        localStorage.removeItem('authToken');
        setIsAuthenticated(false);
        setIsAttested(null);
      }
    } finally {
      setIsCheckingAttestation(false);
    }
  };

  useEffect(() => {
    // Wait until root element is available and WebApp is ready
    const rootElement = document.getElementById('root');
    
    // Only initialize Telegram WebApp features if we're in Telegram environment
    if (webApp && !isAppInitialized && rootElement && isReady && enableTelegramFeatures) {
      try {
        // Basic WebApp initialization that should work in all environments
        webApp.ready();
        webApp.expand();
        
        // Try to use advanced features only if they exist
        // These might not be supported in all versions of Telegram WebApp
        try {
          if (typeof webApp.enableClosingConfirmation === 'function') {
            webApp.enableClosingConfirmation();
          }
        } catch {
          console.warn('WebApp.enableClosingConfirmation not supported');
        }
        
        try {
          if (typeof webApp.disableVerticalSwipes === 'function') {
            webApp.disableVerticalSwipes();
          }
        } catch {
          console.warn('WebApp.disableVerticalSwipes not supported');
        }
        
        try {
          if (typeof webApp.requestFullscreen === 'function') {
            webApp.requestFullscreen();
          }
        } catch {
          console.warn('WebApp.requestFullscreen not supported');
        }
        
        isAppInitialized = true;
      } catch (error) {
        console.error('Error initializing WebApp:', error);
      }
    }
    
    // For non-Telegram environments, just mark as initialized
    if (!enableTelegramFeatures && !isAppInitialized) {
      isAppInitialized = true;
    }
  }, [webApp, isReady, enableTelegramFeatures]);

  // In Telegram environment, we need to wait for WebApp ready state
  // In PWA/browser environment, we need to check authentication
  const isLoading = (isTelegram && !isReady) || 
                   (isAuthenticated === null) ||
                   (isAuthenticated && isCheckingAttestation) &&
                   (isPWADomain);
  
  if (isLoading) {
    return (
      <>
        <PWAInstallPrompt />
        <Loader fullscreen />
      </>
    );
  }

  return (
    <AppContextProvider>
      <ToastProvider>
        <BrowserRouter>
          <Routes>
            {/* 
              Mobile PWA Landing: Show ONLY when:
              - Mobile browser (not desktop, not Telegram)
              - On PWA domain (pwa.secondlane.io, dev-pwa.secondlane.io)
              - User is not authenticated 
              - PWA is NOT installed yet
              
              Shows PWALanding.svg image resized to screen
              If PWA is already installed → redirect to signup
            */}
            <Route 
              path="/pwa-landing" 
              element={
                // Local testing
                // <MobilePWALandingScreen />
                !isMobileBrowser || !isPWADomain || isPWAInstalled
                  ? <Navigate to="/signup" replace />
                  : isAuthenticated 
                    ? <Navigate to="/" replace />
                    : <MobilePWALandingScreen />
              }
            />

            {/* 
              Desktop PWA Landing: Show ONLY when:
              - Desktop OR Mobile browser
              - On mobile domain (m.secondlane.io, dev-m.secondlane.io)
              - NOT in Telegram
              
              Shows QR code and installation instructions
              - Desktop: PWALandingScreenDesktop (full featured landing)
              - Mobile: PWALandingScreenMobile (compact mobile version)
            */}
            <Route 
              path="/landing" 
              element={
                      // Local testing
                      // <PWALandingScreenDesktop />
                      // <MobilePWALandingScreenMobile />
                (isDesktopBrowser || isMobileBrowser) && isMobileDomain
                  ? (isDesktopBrowser 
                      ? <PWALandingScreenDesktop />
                      : <MobilePWALandingScreenMobile />
                    )
                  : <Navigate to="/signup" replace />
              }
            />

            {/* Authentication routes (accessible to all) */}
            <Route path="/login" element={
              isAuthenticated ? <Navigate to="/" replace /> : <LoginScreen />
            } />
            <Route path="/signup" element={
              isAuthenticated ? <Navigate to="/" replace /> : <SignupScreen />
            } />
            <Route path="/forgot-password" element={
              isAuthenticated ? <Navigate to="/" replace /> : <ForgotPasswordScreen />
            } />

            {/* Welcome screen (outside of MainLayout) */}
            <Route path="/welcome" element={
              (isPWA || isBrowser) && isAuthenticated === false ? 
                <Navigate to="/signup" replace /> : 
                <WelcomeScreen />
            } />

            {/* Attestation screen (outside of MainLayout) */}
            <Route path="/attestation" element={
              (isPWA || isBrowser) && isAuthenticated === false
                ? <Navigate to="/signup" replace /> 
                : isAttested 
                  ? <Navigate to="/" replace /> 
                  : <AttestationScreen onAttestationComplete={refreshAttestationStatus} />
            } />

            <Route element={<MainLayout />}>
              {/* 
                Main page routing for unauthenticated users:
                - PWA domain + Mobile + PWA not installed → Mobile PWA Landing (PWALanding.svg)
                - PWA domain + Mobile + PWA installed → Signup
                - Mobile domain + Desktop → Desktop PWA Landing (QR code)
                - Mobile domain + Mobile → Desktop PWA Landing (QR code)
                - Telegram → Signup
              */}
              <Route 
                path="/" 
                element={
                          // <Navigate to="/landing" replace />
                    isAuthenticated === false && isPWADomain && isMobileBrowser && !isPWAInstalled
                      ? <Navigate to="/pwa-landing" replace />
                      : isAuthenticated === false && isPWADomain && isMobileBrowser && isPWAInstalled
                        ? <Navigate to="/signup" replace />
                        : isAuthenticated === false && isMobileDomain && isDesktopBrowser
                          ? <Navigate to="/landing" replace />
                          : isAuthenticated === false && isMobileDomain && isMobileBrowser
                            ? <Navigate to="/landing" replace />
                            : isAuthenticated === false && isTelegram
                              ? <Navigate to="/signup" replace />
                              : isAuthenticated === false
                                ? <Navigate to="/signup" replace />
                                : isAttested === false 
                                  ? <Navigate to="/welcome" replace /> 
                                  : <Navigate to="/deals" replace />
                } 
              />
              
              {/* Protected routes with MainLayout */}
              <Route 
                path="/deals" 
                element={
                  (isPWA || isBrowser) && isAuthenticated === false
                    ? <Navigate to="/login" replace />
                    : isAttested === false 
                      ? <Navigate to="/welcome" replace /> 
                      : <MainLayout><DealsScreen /></MainLayout>
                } 
              />
              <Route 
                path="/notifications" 
                element={
                  (isPWA || isBrowser) && isAuthenticated === false
                    ? <Navigate to="/login" replace />
                    : isAttested === false 
                      ? <Navigate to="/welcome" replace /> 
                      : <NotificationsScreen />
                } 
              />
              <Route 
                path="/notification-settings" 
                element={
                  (isPWA || isBrowser) && isAuthenticated === false
                    ? <Navigate to="/login" replace />
                    : isAttested === false 
                      ? <Navigate to="/welcome" replace /> 
                      : <NotificationSettingsScreen />
                } 
              />
              <Route 
                path="/database" 
                element={
                  (isPWA || isBrowser) && isAuthenticated === false
                    ? <Navigate to="/login" replace />
                    : isAttested === false 
                      ? <Navigate to="/welcome" replace /> 
                      : <MainLayout><DatabaseScreen /></MainLayout>
                } 
              />
              <Route 
                path="/database/project/:projectId" 
                element={
                  (isPWA || isBrowser) && isAuthenticated === false
                    ? <Navigate to="/login" replace />
                    : isAttested === false 
                      ? <Navigate to="/welcome" replace /> 
                      : <ProjectDetailsScreen />
                } 
              />
              <Route 
                path="/portfolio" 
                element={
                  (isPWA || isBrowser) && isAuthenticated === false
                    ? <Navigate to="/login" replace />
                    : isAttested === false 
                      ? <Navigate to="/welcome" replace /> 
                      : <PortfolioScreen />
                } 
              />
              <Route 
                path="/portfolio/:portfolioId" 
                element={
                  (isPWA || isBrowser) && isAuthenticated === false
                    ? <Navigate to="/login" replace />
                    : isAttested === false 
                      ? <Navigate to="/welcome" replace /> 
                      : <PortfolioDetailScreen />
                } 
              />
              <Route 
                path="/portfolio/:portfolioId/create-asset" 
                element={
                  (isPWA || isBrowser) && isAuthenticated === false
                    ? <Navigate to="/login" replace />
                    : isAttested === false 
                      ? <Navigate to="/welcome" replace /> 
                      : <CreatePortfolioAssetScreen />
                } 
              />
              <Route 
                path="/portfolio/:portfolioId/edit-asset/:assetId" 
                element={
                  (isPWA || isBrowser) && isAuthenticated === false
                    ? <Navigate to="/login" replace />
                    : isAttested === false 
                      ? <Navigate to="/welcome" replace /> 
                      : <EditPortfolioAssetScreen />
                } 
              />
              <Route 
                path="/portfolio/asset/:assetId" 
                element={
                  (isPWA || isBrowser) && isAuthenticated === false
                    ? <Navigate to="/login" replace />
                    : isAttested === false 
                      ? <Navigate to="/welcome" replace /> 
                      : <AssetDetailScreen />
                } 
              />
              <Route 
                path="/place-inquiry" 
                element={
                  (isPWA || isBrowser) && isAuthenticated === false
                    ? <Navigate to="/login" replace />
                    : isAttested === false 
                      ? <Navigate to="/welcome" replace /> 
                      : <PlaceInquiryScreen />
                } 
              />
              <Route 
                path="/place-inquiry/:orderId" 
                element={
                  (isPWA || isBrowser) && isAuthenticated === false
                    ? <Navigate to="/login" replace />
                    : isAttested === false 
                      ? <Navigate to="/welcome" replace /> 
                      : <PlaceInquiryScreen />
                } 
              />
              <Route 
                path="/place-rfq" 
                element={
                  (isPWA || isBrowser) && isAuthenticated === false
                    ? <Navigate to="/signup" replace />
                    : isAttested === false 
                      ? <Navigate to="/welcome" replace /> 
                      : <PlaceRFQScreen />
                } 
              />
              <Route 
                path="/place-rfq/:projectId" 
                element={
                  (isPWA || isBrowser) && isAuthenticated === false
                    ? <Navigate to="/signup" replace />
                    : isAttested === false 
                      ? <Navigate to="/welcome" replace /> 
                      : <PlaceRFQScreen />
                } 
              />
              <Route path="*" element={
                (isPWA || isBrowser) && isAuthenticated === false
                  ? <Navigate to="/signup" replace />
                  : <Navigate to="/" replace />
              } />
            </Route>
          </Routes>
        </BrowserRouter>
      </ToastProvider>
    </AppContextProvider>
  );
}

export default App;
